import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Query Key Factory ---
export const scoringOpsKeys = {
  all: ['scoring-ops'] as const,
  roundScores: (eventId: string, roundNumber: number) =>
    [...scoringOpsKeys.all, 'round-scores', eventId, roundNumber] as const,
  teamScores: (eventId: string, teamId: string) =>
    [...scoringOpsKeys.all, 'team-scores', eventId, teamId] as const,
  standings: (eventId: string, filters?: StandingsFilters) =>
    filters
      ? ([...scoringOpsKeys.all, 'standings', eventId, filters] as const)
      : ([...scoringOpsKeys.all, 'standings', eventId] as const),
  winnings: (eventId: string) =>
    [...scoringOpsKeys.all, 'winnings', eventId] as const,
  shortRound: (eventId: string) =>
    [...scoringOpsKeys.all, 'short-round', eventId] as const,
  roundConfigs: (eventId: string) =>
    [...scoringOpsKeys.all, 'round-configs', eventId] as const,
};

// --- Types (aligned with backend schemas) ---
export interface RoundScoreEntry {
  id: string;
  team_id: string;
  round_id: string;
  round_number: number | null;
  time_seconds: number | null;
  caught: boolean;
  penalty_seconds: number | null;
  final_time: number | null;
  left_early_penalty: number | null;
  one_leg_penalty: boolean;
  disqualified: boolean;
  disqualification_reason: string | null;
  scored_at: string | null;
  scored_by: string | null;
  notes: string | null;
  /** Rodeo scoring only: contestant did not perform this run (DNP). */
  did_not_perform?: boolean;
  team_number: number | null;
  member_names: string[] | null;
  combined_handicap: number | null;
  team_status: string | null;
}

export interface RoundScoresResponse {
  data: RoundScoreEntry[];
  round_number: number;
  total: number;
}

export interface StandingEntry {
  placement: number;
  team_id: string;
  team_number: number | null;
  member_names: string[] | null;
  combined_handicap: number | null;
  status: string | null;
  total_runs: number;
  successful_catches: number;
  total_time: number | null;
  average_time: number | null;
  best_time: number | null;
  advanced_to_short_round: boolean;
  /** Rodeo scoring only: every in-scope run is DNP (did not perform). Sorts
   *  below NT/NS and shows a DNP marker. */
  did_not_perform?: boolean;
  /** Rodeo scoring only: entry is excluded from the dynamic prize pool. */
  excluded_from_prize_pool?: boolean;
  /**
   * Total prize won by this finishing place — already includes any ground money
   * folded in. Null/absent when the place won no prize (including when the whole
   * pool was held because no contestant scored).
   */
  prize_amount?: number | null;
  /**
   * Portion of `prize_amount` that is ground money (unclaimed-placement money
   * redistributed among those who scored). Surfaced for completeness; the
   * contestant results UI shows only the folded `prize_amount` total.
   */
  ground_money?: number | null;
  /**
   * Each in-scope round's contribution to `total_time`, ordered by round. `value`
   * is the counted time/score (status 'ok'), null otherwise. Absent from older
   * backends or when a run can't be tied to one round.
   */
  round_results?: Array<{
    round: number;
    value: number | null;
    status: 'ok' | 'no_time' | 'dq' | 'dnp' | 'pending';
  }>;
}

export interface PrizeScopes {
  /** Whether the overall Average payout has prizes. */
  average: boolean;
  /** Round numbers that carry per-round prizes. */
  rounds: number[];
}

export interface StandingsResponse {
  data: StandingEntry[];
  event_id: string;
  through_round: number | null;
  /** Round this ranking is scoped to (null = overall Average). */
  round?: number | null;
  /** Which scopes carry prizes — drives the results scope toggle. */
  prize_scopes?: PrizeScopes;
  /**
   * Prize money held (not paid out) because no contestant scored — carried to
   * the finals. 0 whenever ≥1 contestant scored. Not surfaced in the
   * contestant-facing results UI.
   */
  prize_held?: number;
  total: number;
}

export interface ContestantWinningsEntry {
  /** Competition ranking: equal totals share a place, the next place skips. */
  placement: number;
  /** Null for a member with no linked profile (write-in partner, jackpot team). */
  user_profile_id: string | null;
  name: string;
  total_winnings: number;
  /** Every scope this person earned a share in, Average first then rounds. */
  breakdown: Array<{
    /** null = the overall Average. */
    round: number | null;
    /** This person's share, not the team total. */
    prize_amount: number;
  }>;
}

export interface EventWinningsResponse {
  event_id: string;
  prize_scopes: PrizeScopes;
  /** False when the prize-visibility gate hides amounts; `data` is then empty. */
  prizes_visible: boolean;
  data: ContestantWinningsEntry[];
  total: number;
}

export interface ShortRoundTeam {
  team_id: string;
  team_number: number | null;
  member_names: string[] | null;
  combined_handicap: number | null;
  short_round_position: number | null;
  total_time: number | null;
  successful_catches: number | null;
}

export interface ShortRoundResponse {
  data: ShortRoundTeam[];
  total: number;
}

export interface RoundConfig {
  id: string;
  event_id: string;
  round_number: number;
  is_progressive: boolean;
  is_short_go: boolean;
  advancing_count: number | null;
  short_go_order: string | null;
}

export interface RoundConfigsResponse {
  data: RoundConfig[];
  total: number;
}

interface StandingsFilters {
  through_round?: number;
  /** Isolated-round scope: rank by this round only. Omit for overall Average. */
  round?: number;
}

interface RecordScoreData {
  team_id: string;
  time_seconds?: number;
  caught: boolean;
  penalty_seconds?: number;
  left_early_penalty?: number;
  one_leg_penalty?: boolean;
  disqualified?: boolean;
  disqualification_reason?: string;
  notes?: string;
  /** Rodeo scoring only: contestant did not perform this run (DNP). */
  did_not_perform?: boolean;
  /** Rodeo scoring only: pull this entry out of the dynamic prize pool. */
  excluded_from_prize_pool?: boolean;
  /** Rodeo scoring only: required to scope the run to the active
   *  performance_event. Ignored by the backend for jackpot events. */
  performance_event_id?: string;
}

interface UpdateScoreData {
  time_seconds?: number;
  caught?: boolean;
  penalty_seconds?: number;
  left_early_penalty?: number;
  one_leg_penalty?: boolean;
  disqualified?: boolean;
  disqualification_reason?: string;
  notes?: string;
  did_not_perform?: boolean;
  excluded_from_prize_pool?: boolean;
  performance_event_id?: string;
}

interface UpsertRoundConfigData {
  is_progressive?: boolean;
  is_short_go?: boolean;
  advancing_count?: number;
  short_go_order?: 'slowest_first' | 'fastest_first';
}

interface ShortRoundSetupData {
  order?: 'slowest_first' | 'fastest_first';
  team_ids?: string[];
}

interface AdvanceTeamsData {
  override_count?: number;
}

interface ReorderTeamsData {
  team_orders: Array<{ team_id: string; order: number }>;
}

interface ShortRoundReorderData {
  team_orders: Array<{ team_id: string; position: number }>;
}

interface UpdateTeamStatusData {
  status: string;
  reason?: string;
}

// --- Query Hooks ---
export function useRoundScores(
  eventId: string,
  roundNumber: number,
  performanceEventId?: string,
) {
  const qs = performanceEventId ? `?performance_event_id=${performanceEventId}` : '';
  return useQuery({
    queryKey: performanceEventId
      ? ([...scoringOpsKeys.roundScores(eventId, roundNumber), performanceEventId] as const)
      : scoringOpsKeys.roundScores(eventId, roundNumber),
    queryFn: () =>
      httpClient.get<RoundScoresResponse>(
        `/scoring-ops/event/${eventId}/round/${roundNumber}/scores${qs}`,
      ),
    enabled: !!eventId && roundNumber > 0,
    retry: false,
    staleTime: 10_000,
  });
}

export function useTeamScores(eventId: string, teamId: string) {
  return useQuery({
    queryKey: scoringOpsKeys.teamScores(eventId, teamId),
    queryFn: () =>
      httpClient.get(
        `/scoring-ops/event/${eventId}/team/${teamId}/scores`,
      ),
    enabled: !!eventId && !!teamId,
    retry: false,
    staleTime: 10_000,
  });
}

/**
 * `keepPreviousData`, but only within one event: a scope change (round tab,
 * through-round filter) keeps the previous rows on screen until the new ones
 * arrive, while switching to a different event never shows the old event's
 * rows under the new one. Scoring-ops keys carry the event id at index 2.
 */
function keepPreviousForEvent<T>(eventId: string) {
  return (previous: T | undefined, previousQuery?: { queryKey: readonly unknown[] }) =>
    previousQuery?.queryKey[2] === eventId ? keepPreviousData(previous) : undefined;
}

/** Query options for an event's standings — shared by the hook and prefetches. */
export function eventStandingsQueryOptions(eventId: string, filters?: StandingsFilters) {
  const qs = new URLSearchParams();
  if (filters?.through_round) qs.set('through_round', String(filters.through_round));
  if (filters?.round) qs.set('round', String(filters.round));
  const query = qs.toString() ? `?${qs}` : '';
  return queryOptions({
    queryKey: scoringOpsKeys.standings(eventId, filters),
    queryFn: () =>
      httpClient.get<StandingsResponse>(
        `/scoring-ops/event/${eventId}/standings${query}`,
      ),
    retry: false,
  });
}

export function useEventStandings(
  eventId: string,
  filters?: StandingsFilters,
  options?: { refetchInterval?: number; enabled?: boolean },
) {
  return useQuery({
    ...eventStandingsQueryOptions(eventId, filters),
    enabled: !!eventId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    placeholderData: keepPreviousForEvent<StandingsResponse>(eventId),
  });
}

export function useEventWinnings(
  eventId: string,
  options?: { refetchInterval?: number; enabled?: boolean },
) {
  return useQuery({
    queryKey: scoringOpsKeys.winnings(eventId),
    queryFn: () =>
      httpClient.get<EventWinningsResponse>(
        `/scoring-ops/event/${eventId}/winnings`,
      ),
    enabled: !!eventId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    placeholderData: keepPreviousForEvent<EventWinningsResponse>(eventId),
    retry: false,
  });
}

export function useShortRound(eventId: string) {
  return useQuery({
    queryKey: scoringOpsKeys.shortRound(eventId),
    queryFn: () =>
      httpClient.get<ShortRoundResponse>(
        `/scoring-ops/event/${eventId}/short-round`,
      ),
    enabled: !!eventId,
  });
}

export function useRoundConfigs(eventId: string) {
  return useQuery({
    queryKey: scoringOpsKeys.roundConfigs(eventId),
    queryFn: () =>
      httpClient.get<RoundConfigsResponse>(
        `/scoring-ops/event/${eventId}/round-configs`,
      ),
    enabled: !!eventId,
  });
}

// --- Mutation Hooks ---
export function useRecordScore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      roundNumber,
      ...data
    }: RecordScoreData & { eventId: string; roundNumber: number }) =>
      httpClient.post(
        `/scoring-ops/event/${eventId}/round/${roundNumber}/score`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
      // Invalidate draw-ops on a no-catch (team gets eliminated) OR when the
      // per-entry prize-exclusion flag was changed (it lives on the synthetic
      // team, so the draw-teams cache must refresh to stay in sync).
      if (!variables.caught || variables.excluded_from_prize_pool !== undefined) {
        qc.invalidateQueries({ queryKey: ['draw-ops'] });
      }
    },
  });
}

export function useUpdateScore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      roundNumber,
      teamId,
      ...data
    }: UpdateScoreData & {
      eventId: string;
      roundNumber: number;
      teamId: string;
    }) =>
      httpClient.put(
        `/scoring-ops/event/${eventId}/round/${roundNumber}/team/${teamId}/score`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
      if (variables.caught === false || variables.excluded_from_prize_pool !== undefined) {
        qc.invalidateQueries({ queryKey: ['draw-ops'] });
      }
    },
  });
}

export function useAdvanceTeams() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      roundNumber,
      ...data
    }: AdvanceTeamsData & { eventId: string; roundNumber: number }) =>
      httpClient.post(
        `/scoring-ops/event/${eventId}/round/${roundNumber}/advance`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
    },
  });
}

export function useSetupShortRound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: ShortRoundSetupData & { eventId: string }) =>
      httpClient.post(
        `/scoring-ops/event/${eventId}/short-round/setup`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: scoringOpsKeys.shortRound(variables.eventId),
      });
    },
  });
}

export function useUpsertRoundConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      roundNumber,
      ...data
    }: UpsertRoundConfigData & { eventId: string; roundNumber: number }) =>
      httpClient.post(
        `/scoring-ops/event/${eventId}/round-configs/${roundNumber}`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: scoringOpsKeys.roundConfigs(variables.eventId),
      });
    },
  });
}

export function useDeleteRoundConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      roundNumber,
    }: {
      eventId: string;
      roundNumber: number;
    }) =>
      httpClient.delete(
        `/scoring-ops/event/${eventId}/round-configs/${roundNumber}`,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: scoringOpsKeys.roundConfigs(variables.eventId),
      });
    },
  });
}

export function useReorderTeamsInRound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      roundNumber,
      ...data
    }: ReorderTeamsData & { eventId: string; roundNumber: number }) =>
      httpClient.post(
        `/scoring-ops/event/${eventId}/round/${roundNumber}/reorder`,
        data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
    },
  });
}

export function useUpdateTeamStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      teamId,
      ...data
    }: UpdateTeamStatusData & { eventId: string; teamId: string }) =>
      httpClient.request(
        `/scoring-ops/event/${eventId}/team/${teamId}/status`,
        { method: 'PATCH', body: JSON.stringify(data) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
    },
  });
}

// --- New Hooks (from migration) ---

export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, teamId }: { eventId: string; teamId: string }) =>
      httpClient.delete(
        `/scoring-ops/event/${eventId}/team/${teamId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
    },
  });
}

export function useDeleteAllTeams() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId }: { eventId: string }) =>
      httpClient.delete(
        `/scoring-ops/event/${eventId}/teams`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
    },
  });
}

export function useReorderShortRound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      ...data
    }: ShortRoundReorderData & { eventId: string }) =>
      httpClient.put(
        `/scoring-ops/event/${eventId}/short-round/reorder`,
        data,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: scoringOpsKeys.shortRound(variables.eventId),
      });
    },
  });
}

export function useBatchRecalculate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId }: { eventId: string }) =>
      httpClient.post(
        `/scoring-ops/event/${eventId}/recalculate`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
    },
  });
}

export function useClearRoundScores() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      roundNumber,
      performanceEventId,
    }: {
      eventId: string;
      roundNumber: number;
      performanceEventId?: string;
    }) => {
      const qs = performanceEventId ? `?performance_event_id=${performanceEventId}` : '';
      return httpClient.request(
        `/scoring-ops/event/${eventId}/round/${roundNumber}/scores${qs}`,
        { method: 'DELETE' },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
    },
  });
}

export function useDeleteScore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scoreId }: { scoreId: string }) =>
      httpClient.delete(`/round-performances/${scoreId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scoringOpsKeys.all });
    },
  });
}
